import React, { useContext, useEffect, useState } from 'react';
import { Box, Icon, Icons, Typography } from '@nextiva/next-ui-components';
import { useHistory } from 'react-router-dom';
import { FeatureContext } from 'components/AccessResolvers/featureContext';

import { SvgUserIcon } from 'components/SvgIcon';
import {
  ImportButton,
  ImportText,
  Tile,
  TileIcon,
  StyledBox,
} from './DataImport.styles';
import { DataImportContext } from './context';
import { screens } from '.';

interface Props {
  title: string;
  clickHandler: () => void;
  setStartImport?: (newValue: string) => void;
}

export const screenEntities = {
  USER_CREATE: 'USER_CREATE',
  USER_UPDATE: 'USER_UPDATE',
  TEAM_CREATE: 'TEAM_CREATE',
  LOCATION_CREATE: 'LOCATION_CREATE',
};

const TITLES = {
  LOCATIONS: 'Locations',
  USERS: 'Users',
  TEAMS: 'Teams',
  PHONES_AND_DEVICES: 'Phones & Devices',
};

const getSubtitle = {
  [TITLES.USERS]: 'Import people that can sign in as users for your company.',
  [TITLES.TEAMS]: 'Import team names, phone numbers, and members.',
  [TITLES.LOCATIONS]: 'Import business locations where people work.',
  [TITLES.PHONES_AND_DEVICES]:
    'Import phones & devices that your company uses.',
};

const getIcon = {
  [TITLES.USERS]: (
    <StyledBox>
      <SvgUserIcon />
    </StyledBox>
  ),
  [TITLES.TEAMS]: <TileIcon icon={Icons.teamLight} sx={{ width: '50px' }} />,
  [TITLES.LOCATIONS]: <TileIcon icon={Icons.buildingLight} />,
  [TITLES.PHONES_AND_DEVICES]: <TileIcon icon={Icons.telephoneLight} />,
};

const ImportTile = ({ title, clickHandler, setStartImport }: Props) => {
  const { jobLocks } = useContext(DataImportContext);
  const [isImportInProgress, setIsImportInProgress] = useState(false);
  const {
    featureState: { bulkUsersEdit },
  } = useContext(FeatureContext);
  const history = useHistory();

  useEffect(() => {
    if (
      (title === 'Locations' &&
        jobLocks?.includes(screenEntities.LOCATION_CREATE)) ||
      (title === 'Phones & Devices' &&
        jobLocks?.includes(screens.PHONES_AND_DEVICES_IMPORT)) ||
      (title === 'Teams' && jobLocks?.includes(screenEntities.TEAM_CREATE)) ||
      (title === 'Users' && jobLocks?.includes(screenEntities.USER_CREATE)) ||
      (title === 'Users' && jobLocks?.includes(screenEntities.USER_UPDATE))
    ) {
      setIsImportInProgress(true);
    } else {
      setIsImportInProgress(false);
    }
  }, [title, jobLocks]);

  return (
    <Tile
      elevation={isImportInProgress ? 0 : 1}
      sx={
        isImportInProgress && {
          borderWidth: '1px',
          borderColor: (theme) => theme.palette.secondary.light,
          borderStyle: 'solid',
        }
      }
      variant="elevation"
    >
      <Box
        alignItems="center"
        component="div"
        display="flex"
        height={100}
        width="100%"
        flexDirection="column"
      >
        {getIcon[title]}
        <Typography variant="h6">{title}</Typography>
        <ImportText variant="body2">{getSubtitle[title]}</ImportText>
        {isImportInProgress ? (
          <Box marginTop="20px" display="inline-flex" alignItems="center">
            <Icon
              icon={Icons.exclamation}
              sx={{
                marginRight: '8px',
                color: (theme) => theme.palette.info.main,
              }}
            />
            <Typography fontWeight="400" fontSize="12px">
              Import in progress
            </Typography>
          </Box>
        ) : (
          <>
            <ImportButton
              color="primary"
              size="medium"
              variant="outlined"
              onClick={clickHandler}
              data-testid={`PlatformAdminUI-DataImportTiles-ImportButton-${title.replace(
                /\s+/g,
                '-',
              )}`}
            >
              Import new
            </ImportButton>
            {bulkUsersEdit && title === TITLES.USERS && (
              <ImportButton
                color="primary"
                size="medium"
                variant="text"
                onClick={() => {
                  history.push({
                    state: screens.USER_UPDATE,
                  });
                  setStartImport(screens.USER_UPDATE);
                }}
                data-testid={`PlatformAdminUI-DataImportTiles-EditButton-${title.replace(
                  /\s+/g,
                  '-',
                )}`}
              >
                Edit existing
              </ImportButton>
            )}
          </>
        )}
      </Box>
    </Tile>
  );
};

export default ImportTile;
